import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";

import { SyntheticEvent, useEffect, useRef, useState } from "react";

import { Box, Button, useToast } from "@/storybook-components";
import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import {
  fetchIdeas,
  saveIdea,
  updateOneIdea
} from "@/redux/features/brainstormers/thunk";

import ContentDrawer from "../common/drawer";

import { fileUpload, privateBucketFileUpload } from "@/models";
import CreateIdea from "./create/index";
import { setStatus } from "@/redux/features/questions/slice";
import { setCloseIdeaStatus } from "@/redux/features/brainstormers/slice";
import useUser from "@/hooks/useUser";
import useAuthGate from "@/hooks/useAuthGate";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";
import { getPrivateFileDownloadUrl } from "@/helpers/private-file-url";
import { getPrivateFileSubmissionUrlFromRecord } from "@/helpers/private-file-submission";
import { scrollIntoView } from "@/lib";
import { getToolLocaleKeyFromLayout } from "@/helpers/get-tool-locale-key-from-layout";
import { toSentenceCase } from "@/helpers/to-sentence-case";
import type { IContentParams } from "@/types/brainstormers";
import { useIsPrivateIdeaMediaBucketEnabled } from "@/hooks/toggle";

interface PostDrawerProps {
  onOpen: () => void;
  open: boolean;
  onClose: () => void;
  type: string;
  isEditPost: boolean;
  editHandler?: (e: boolean) => void;
  currentPostData?: any;
  defaultCategoryId?: string | null;
  refreshParams?: Partial<IContentParams>;
}

const OpenPostDrawer = ({
  onOpen,
  open,
  onClose,
  type,
  isEditPost,
  editHandler,
  currentPostData,
  defaultCategoryId,
  refreshParams
}: PostDrawerProps): JSX.Element => {
  const { translate } = useTranslator();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { query } = useRouter();
  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);
  const { features } = useAppSelector((state) => state.features);
  const isNewCookieConsentEnabled = features?.newCookieConsentManager?.enabled;
  const isSiteAdminToggleEnabled = features?.siteAdminModeration?.enabled;
  const isPrivateBucketEnabled = useIsPrivateIdeaMediaBucketEnabled();

  const user = useAppSelector((state) => state.user);
  const anonymousUser = useAppSelector((state) => state.anonymousUser);

  const { token } = fetchAuthToken({ user, anonymousUser });

  const { brainstormer, status } = useAppSelector(
    (state) => state?.brainstormers
  );
  const {
    layout,
    allowUnverifiedParticipation,
    allowSiteAdminModeration,
    baCategoryConfigAttributes,
    toolConfigurationAttributes
  } = brainstormer;

  const contributionName =
    toolConfigurationAttributes?.preferences?.contributionName || "";

  const { unitCap } = baCategoryConfigAttributes || {};

  const { errors } = useAppSelector((state) => state.questions);
  const { isRegisteredUserLoggedIn } = useUser();
  const { suggestProfileCompletion } = useAuthGate();

  const routerPath = router.asPath;
  const { action } = router.query;
  const toast = useToast();

  useEffect(() => {
    if (action === "new_idea") onOpen();
  }, [onOpen, action]);

  const [ideaSubmitting, setIdeaSubmitting] = useState(false);

  const initialPostData = {
    name: "",
    description: "",
    mediaUrl: "",
    backgroundColourId: null,
    categoryId: isEditPost ? null : (defaultCategoryId ?? null),
    ...(layout === "BudgetAllocator"
      ? { baItemConfigAttributes: { unitCap: null } }
      : {})
  };
  const [postData, setPostData] = useState(
    isEditPost
      ? {
          name: currentPostData?.name,
          description: currentPostData?.description,
          mediaUrl:
            getPrivateFileDownloadUrl(currentPostData, "ideas") ??
            currentPostData?.mediaUrl,
          backgroundColourId: currentPostData?.backgroundColourId,
          categoryId: currentPostData?.categoryId || null,
          ...(layout === "BudgetAllocator"
            ? {
                baItemConfigAttributes: {
                  unitCap: currentPostData?.baItemConfig?.unitCap || null
                }
              }
            : {})
        }
      : initialPostData
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialQuestionError = {
    name: "",
    description: "",
    mediaUrl: "",
    terms: "",
    backgroundColourId: "",
    ...(layout === "BudgetAllocator"
      ? { baItemConfigAttributes: { unitCap: "" } }
      : {})
  };
  const [questionError, setQuestionError] = useState(initialQuestionError);
  const [hasErrorInSubmission, setHasErrorInSubmission] = useState(false);
  const submitInFlightRef = useRef(false);

  const closeDrawer = () => {
    onClose();
  };

  const handleCloseComplete = async () => {
    removeQueryParam();
    if (hasErrorInSubmission) await setHasErrorInSubmission(false);
    if (isEditPost) {
      dispatch(setCloseIdeaStatus("closingIdea"));
      editHandler(false);
    }
    await setPostData(initialPostData);
    await setQuestionError(initialQuestionError);
  };

  const cancelPost = () => {
    closeDrawer();
  };

  const removeQueryParam = () => {
    const [updatedPath] = routerPath.split("?");
    router.replace(updatedPath, undefined, { shallow: true });
  };

  const createOrUpdateIdea = async () => {
    const {
      name,
      description,
      mediaUrl,
      backgroundColourId,
      baItemConfigAttributes,
      categoryId
    } = postData;

    try {
      let imageUrl = mediaUrl && !mediaUrl.startsWith("data:") ? mediaUrl : "";
      if (!imageUrl && mediaUrl?.startsWith("data:")) {
        const file = await fetch(mediaUrl).then((res) => res.blob());
        const projectId = query.projectId as string;
        const brainstormerId = query.brainstormerId as string;

        const uploadedFile = isPrivateBucketEnabled
          ? await privateBucketFileUpload(
              token,
              baseUrl,
              file,
              projectId,
              brainstormerId,
              { tool: "brainstormer" }
            )
          : await fileUpload(token, baseUrl, file);

        imageUrl = getPrivateFileSubmissionUrlFromRecord(uploadedFile, "ideas");

        if (!imageUrl) {
          throw new Error("Missing media-url from idea_files upload");
        }
      }

      let response;
      if (isEditPost) {
        response = await dispatch(
          updateOneIdea({
            ideaId: currentPostData.id,
            name,
            description,
            mediaUrl: imageUrl,
            backgroundColourId,
            categoryId,
            ...(layout === "BudgetAllocator" ? { baItemConfigAttributes } : {})
          })
        );
      } else {
        response = await dispatch(
          saveIdea({
            name,
            description,
            mediaUrl: imageUrl,
            backgroundColourId,
            categoryId,
            ...(layout === "BudgetAllocator" ? { baItemConfigAttributes } : {})
          })
        );
      }

      if (!!response && !response.error) {
        const editableToastMsg = isEditPost
          ? translate("brainstormers.youHaveUpdatedYourContent")
          : translate("brainstormers.youHavePostedYourContent");
        const toastTitle =
          allowSiteAdminModeration && isSiteAdminToggleEnabled
            ? translate("brainstormers.yourPostIsUnderReview")
            : editableToastMsg;

        toast({
          id: "idea-toast",
          description: toastTitle,
          status: "success",
          isClosable: true
        });
        if (!isEditPost) {
          suggestProfileCompletion({
            reason: "profileGate.optionalSuggestion",
            source: "ideas"
          });
        }
        setTimeout(() => {
          cancelPost();
        }, 100);
      } else {
        toast({
          id: "idea-toast",
          title: translate("errors.sorrySomethingWentWrong"),
          status: "error",
          isClosable: true
        });
      }
      // eslint-disable-next-line no-unused-vars
    } catch (err) {
      toast({
        id: "idea-toast",
        title: translate("errors.sorrySomethingWentWrong"),
        status: "error",
        isClosable: true
      });
      setTimeout(() => {
        cancelPost();
      }, 100);
    }
  };

  const onSubmitHandler = async (e: SyntheticEvent) => {
    e.preventDefault();

    if (submitInFlightRef.current || isSubmitting) return;
    submitInFlightRef.current = true;
    setIsSubmitting(true);

    let errorCount = 0;
    if (!postData.name) {
      await setQuestionError((prevQuestionError) => ({
        ...prevQuestionError,
        name: "blank"
      }));
      errorCount += 1;
    }

    if (layout === "BudgetAllocator") {
      if (!postData.baItemConfigAttributes.unitCap) {
        setQuestionError((prevQuestionError) => ({
          ...prevQuestionError,
          baItemConfigAttributes: { unitCap: "blank" }
        }));
        errorCount += 1;
      } else {
        if (postData.baItemConfigAttributes.unitCap <= 0) {
          setQuestionError((prevQuestionError) => ({
            ...prevQuestionError,
            baItemConfigAttributes: { unitCap: "greaterThanZero" }
          }));
          errorCount += 1;
        } else if (
          unitCap &&
          postData.baItemConfigAttributes.unitCap > unitCap
        ) {
          setQuestionError((prevQuestionError) => ({
            ...prevQuestionError,
            baItemConfigAttributes: { unitCap: "exceedsMaximumBudget" }
          }));
          errorCount += 1;
        }
      }
    }

    if (!postData.mediaUrl && !postData.backgroundColourId) {
      setQuestionError((prevQuestionError) => ({
        ...prevQuestionError,
        backgroundColourId: "empty",
        mediaUrl: "empty"
      }));
      errorCount += 1;
    }

    if (allowUnverifiedParticipation && !isRegisteredUserLoggedIn) {
      if (errors?.email) errorCount += 1;

      if (errorCount <= 0) {
        setIdeaSubmitting(true);
        dispatch(setStatus("saving"));
      }
    }

    if (errorCount > 0) {
      setHasErrorInSubmission(true);
      submitInFlightRef.current = false;
      setIsSubmitting(false);

      // Scroll to first error and focus on it
      setTimeout(() => {
        const errorElement = document.querySelector(".ehq-question-error");
        if (errorElement) {
          const focusableElement = errorElement.querySelector(
            "input, button, textarea, select"
          ) as HTMLElement;

          if (focusableElement) {
            focusableElement.focus();
          }

          scrollIntoView(".ehq-question-error");
        }
      }, 100);

      return false;
    }

    setHasErrorInSubmission(false);

    try {
      await createOrUpdateIdea();
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }

    dispatch(setStatus("idle"));
    setIdeaSubmitting(false);

    // refresh and update the board with new post
    if (!isEditPost) {
      await dispatch(
        fetchIdeas({
          projectId: query.projectId as string,
          brainstormerId: query.brainstormerId as string,
          pageSize: 20,
          page: 1,
          sortBy: refreshParams?.sortBy,
          sortOrder: refreshParams?.sortOrder,
          search: refreshParams?.search,
          categoryId: refreshParams?.categoryId
        })
      );
    }
  };

  const FooterButton = ({
    variant,
    onClickHandler,
    text,
    loading,
    disabled = false,
    "data-testid": footerTestId
  }: {
    variant: string;
    onClickHandler?: (e: SyntheticEvent) => void;
    text: string;
    loading: boolean;
    disabled?: boolean;
    "data-testid"?: string;
  }) => {
    const unverifiedParticipationCheck =
      isRegisteredUserLoggedIn || allowUnverifiedParticipation;
    return (
      <Button
        w={{ base: 160, lg: 120 }}
        size={{ base: "lg", xl: "md" }}
        maxW={160}
        px={4}
        py={2}
        type="button"
        variant={variant}
        onClick={onClickHandler}
        loading={loading}
        disabled={disabled || !unverifiedParticipationCheck}
        data-testid={footerTestId}
      >
        {text}
      </Button>
    );
  };

  const contributionLocaleKey = getToolLocaleKeyFromLayout(layout);

  const title =
    type === "create"
      ? translate("tools.createContribution", {
          contributionName:
            contributionName.trim() ||
            translate(`tools.${contributionLocaleKey}.createContribution`)
        })
      : translate("tools.editContribution", {
          contributionName:
            contributionName.trim() ||
            translate(`tools.${contributionLocaleKey}.editContribution`)
        });

  const contentDrawerZIndex = isNewCookieConsentEnabled ? 1200 : 1000;
  return (
    <ContentDrawer
      open={open}
      onClose={closeDrawer}
      onCloseComplete={handleCloseComplete}
      header={toSentenceCase(title)}
      zIndex={contentDrawerZIndex}
      trapFocus
      data-testid={isEditPost ? "ideas-edit-drawer" : "ideas-create-drawer"}
      content={
        <CreateIdea
          postData={postData}
          setPostData={setPostData}
          questionError={questionError}
          setQuestionError={setQuestionError}
          hasErrorInSubmission={hasErrorInSubmission}
          ideaSubmitting={ideaSubmitting}
          isEditPost={isEditPost}
          currentPostData={currentPostData}
          onSubmit={onSubmitHandler}
        />
      }
      footer={
        <Box
          display="flex"
          alignItems="flex-start"
          alignSelf="stretch"
          justifyContent={{ base: "space-between", lg: "flex-end" }}
          gap={6}
          px={4}
          width="full"
        >
          <FooterButton
            variant="secondary"
            onClickHandler={cancelPost}
            text={translate("common.cancel")}
            loading={false}
            data-testid="ideas-create-drawer-cancel"
          />
          <FooterButton
            variant="primary"
            onClickHandler={onSubmitHandler}
            text={
              isEditPost ? translate("common.update") : translate("common.post")
            }
            loading={isSubmitting || status === "ideaSaving" || ideaSubmitting}
            disabled={
              isSubmitting ||
              status === "ideaSaving" ||
              ideaSubmitting ||
              !!questionError?.description
            }
            data-testid={
              isEditPost
                ? "ideas-edit-drawer-submit"
                : "ideas-create-drawer-submit"
            }
          />
        </Box>
      }
    />
  );
};

export default OpenPostDrawer;
